import { ref, computed, watch } from "vue";
import type { Ref } from "vue";
import {
  ListScheduledDeliveryQuery,
  ListScheduledDeliveryQueryVariables,
  ListScheduledDeliveryDocument,
  OperationInput,
  ScheduledDeliveryListInput,
  GetScheduledDeliverytCountQuery,
  GetScheduledDeliverytCountQueryVariables,
  GetScheduledDeliverytCountDocument,
  ScheduledDeliveryCountInput,
  GetShopQuery,
} from "@/generated/graphql";
import { localStorageGetCustomerCode } from "@/common/localStorageUtil";
import { generateApolloClient } from "@/common/apolloClientUtil";
import { displayPagingRange as calcDisplayPagingRange } from "@/common/common";
import { ApolloQueryResult } from "@apollo/client/core";
import type { ListData, ListOptions, ShopData } from "../types";

/**
 * 一覧テーブル表示に必要なデータ整形と列情報を提供する composable。
 *
 * @returns テーブル描画に利用する列定義と表示用データ
 */
export const useScheduledDeliveryTable = (params: {
  chainSelectValues: Ref<{ chain_code: string; name: string } | null>;
  shopSelectFromValues: Ref<{ shop_code: string; name: string }>;
  shopSelectToValues: Ref<{ shop_code: string; name: string }>;
  shopData: Ref<ShopData | null | undefined>;
  operationInput: OperationInput;
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  getShopData: (
    shop_code: string
  ) => Promise<ApolloQueryResult<GetShopQuery> | undefined>;
}) => {
  const listLoading = ref<boolean>(false);

  const listOptions = ref<ListOptions>({
    page: 1,
    itemsPerPage: 10,
    sortBy: [],
    sortDesc: [],
    groupBy: [],
    groupDesc: [],
    multiSort: false,
    mustSort: false,
  });

  // 店舗間定期配達一覧 データ
  const listDataArray = ref<ListData[]>([]);

  // 店舗間定期配達一覧 絞込み後の全データ数
  const listDataTotalCount = ref<number>(0);

  // 店舗間定期配達一覧 ページ数
  const pageCount = computed(() =>
    Math.max(
      1,
      Math.ceil(
        listDataTotalCount.value / (listOptions.value.itemsPerPage || 1)
      )
    )
  );

  // 選択されたチェックボックス
  const selectListCheckbox = ref<ListData[]>([]);

  //ページング範囲の表示
  const displayPagingRange = ref<string>("");

  // 検索ボタンからの呼び出し時にwatchをスキップするフラグ
  const isSearchButtonClick = ref(false);

  const getRowProps = (item: ListData) => {
    console.log(item);
    return {
      class: "bk_gray_1",
    };
  };

  /**
   * 絞り込み検索
   */
  const searchQuery = async (): Promise<void> => {
    try {
      //画面表示時はエラーメッセージを表示せず終了
      if (!params.chainSelectValues.value?.chain_code) {
        return;
      }

      listLoading.value = true;

      //graphql接続
      const apolloClient = await generateApolloClient();

      // 店舗間定期配達一覧 input [limit 取得件数 offset:取得開始位置]
      const listInput: ScheduledDeliveryListInput = {
        chain_code: params.chainSelectValues.value?.chain_code
          ? params.chainSelectValues.value?.chain_code
          : "",
        customer_code: localStorageGetCustomerCode(),
        delivery_shop_code:
          params.shopSelectToValues.value !== undefined &&
          params.shopSelectToValues.value !== null &&
          params.shopSelectToValues.value.shop_code !== ""
            ? params.shopSelectToValues.value.shop_code
            : null,
        shop_code:
          params.shopSelectFromValues.value !== undefined &&
          params.shopSelectFromValues.value !== null &&
          params.shopSelectFromValues.value.shop_code !== ""
            ? params.shopSelectFromValues.value.shop_code
            : null,
        limit: listOptions.value.itemsPerPage,
        offset: (listOptions.value.page - 1) * listOptions.value.itemsPerPage,
      };

      // 店舗間定期配達一覧作成 logInput
      params.operationInput.detail = "店舗間定期配達一覧";
      params.operationInput.display_category = "一覧表示";

      // 店舗間定期配達一覧取得
      let result: ApolloQueryResult<ListScheduledDeliveryQuery>;
      try {
        result = await apolloClient.query<
          ListScheduledDeliveryQuery,
          ListScheduledDeliveryQueryVariables
        >({
          query: ListScheduledDeliveryDocument,
          variables: {
            input: listInput,
            logInput: params.operationInput,
          },
          fetchPolicy: "no-cache",
        });
      } catch (err: unknown) {
        console.error(err);
        params.errdialog.value = true;
        if (err instanceof Error) {
          params.errMessage.value = err.message;
        } else {
          params.errMessage.value =
            "店舗間定期配達一覧データの取得に失敗しました。";
        }
        return;
      }

      if (
        result.data.listScheduledDelivery === undefined ||
        result.data.listScheduledDelivery === null
      ) {
        // 店舗間定期配達一覧取得 エラー
        params.errdialog.value = true;
        params.errMessage.value =
          "店舗間定期配達一覧データの取得に失敗しました。";
        return;
      }

      // 店舗間定期配達一覧のデータを作成
      let _listDataArray: ListData[] = [];
      const list = result.data.listScheduledDelivery;
      if (list !== undefined && list !== null && list?.length > 0) {
        // 出荷元店舗、配達先店舗取得
        let shop_code_from_ary: string[] = [];
        let shop_code_from_date: { [key: string]: { shop_name: string } } = {};
        let shop_code_to_ary: string[] = [];
        let shop_code_to_date: {
          [key: string]: { shop_name: string; address: string };
        } = {};
        let shop_to_address = "";
        for (const value of list) {
          if (
            value !== null &&
            value !== undefined &&
            (shop_code_from_ary.length == 0 ||
              (shop_code_from_ary.length > 0 &&
                shop_code_from_ary.indexOf(value.shop_code) == -1))
          ) {
            try {
              // 出荷元店舗情報取得
              const shop_data_from = await params.getShopData(value.shop_code);

              params.shopData.value = undefined;
              params.shopData.value = shop_data_from!.data!.getShop!;
              shop_code_from_date[value.shop_code] = {
                shop_name: params.shopData.value!.name,
              };
            } catch (err: unknown) {
              console.error(err);
              params.errdialog.value = true;
              if (err instanceof Error) {
                params.errMessage.value = err.message;
              } else {
                params.errMessage.value = "予期しないエラーが発生しました。";
              }
              return;
            }
            shop_code_from_ary.push(value.shop_code);
          }
          if (
            value !== null &&
            value !== undefined &&
            (shop_code_to_ary.length == 0 ||
              (shop_code_to_ary.length > 0 &&
                shop_code_to_ary.indexOf(value.delivery_shop_code) == -1))
          ) {
            try {
              // 配達先店舗情報取得
              const shop_data_to = await params.getShopData(
                value.delivery_shop_code
              );

              params.shopData.value = undefined;
              params.shopData.value = shop_data_to!.data!.getShop!;
              shop_to_address =
                "〒" +
                params.shopData.value!.zip_code +
                "\r\n" +
                params.shopData.value!.address1 +
                " " +
                params.shopData.value!.address2;
              if (
                params.shopData.value!.address3 !== undefined &&
                params.shopData.value!.address3 !== null &&
                params.shopData.value!.address3 !== ""
              ) {
                shop_to_address += " " + params.shopData.value!.address3;
              }
              shop_code_to_date[value.delivery_shop_code] = {
                shop_name: params.shopData.value!.name,
                address: shop_to_address,
              };
            } catch (err: unknown) {
              console.error(err);
              params.errdialog.value = true;
              if (err instanceof Error) {
                params.errMessage.value = err.message;
              } else {
                params.errMessage.value = "予期しないエラーが発生しました。";
              }
              return;
            }

            shop_code_to_ary.push(value.delivery_shop_code);
          }
        }

        for (const value of list) {
          if (value !== undefined && value !== null) {
            _listDataArray.push({
              scheduled_delivery_no: value.scheduled_delivery_no,
              chainCode: params.chainSelectValues.value
                ? params.chainSelectValues.value.chain_code
                : "",
              shopCodeFrom: value.shop_code,
              shopNameFrom: shop_code_from_date[value.shop_code].shop_name,
              shopCodeTo: value.delivery_shop_code,
              shopNameTo:
                shop_code_to_date[value.delivery_shop_code].shop_name,
              deliveryAddress:
                shop_code_to_date[value.delivery_shop_code].address,
              week: "",
              mon_to_deliver: value.mon_to_deliver!,
              tue_to_deliver: value.tue_to_deliver!,
              wed_to_deliver: value.wed_to_deliver!,
              thu_to_deliver: value.thu_to_deliver!,
              fri_to_deliver: value.fri_to_deliver!,
              sat_to_deliver: value.sat_to_deliver!,
              sun_to_deliver: value.sun_to_deliver!,
              blackoutDateSetting: "",
              deliveryIdName:
                value.delivery_id === 99
                  ? value.area_name + "　定期便"
                  : value.area_name +
                    "　" +
                    String(value.delivery_id) +
                    "便",
              deliveryTime:
                value.irregular_delivery_start_time.substr(0, 5) +
                "～" +
                value.irregular_delivery_end_time.substr(0, 5),
            });
          }
        }
      }

      listDataArray.value = _listDataArray;

      // 店舗間定期配達一覧件数取得
      const ListCntInput: ScheduledDeliveryCountInput = {
        chain_code: params.chainSelectValues.value!.chain_code,
        customer_code: localStorageGetCustomerCode(),
        shop_code:
          params.shopSelectFromValues.value !== undefined &&
          params.shopSelectFromValues.value !== null &&
          params.shopSelectFromValues.value.shop_code !== ""
            ? params.shopSelectFromValues.value.shop_code
            : null,
        delivery_shop_code:
          params.shopSelectToValues.value !== undefined &&
          params.shopSelectToValues.value !== null &&
          params.shopSelectToValues.value.shop_code !== ""
            ? params.shopSelectToValues.value.shop_code
            : null,
      };

      let result_cnt: ApolloQueryResult<GetScheduledDeliverytCountQuery>;
      try {
        result_cnt = await apolloClient.query<
          GetScheduledDeliverytCountQuery,
          GetScheduledDeliverytCountQueryVariables
        >({
          query: GetScheduledDeliverytCountDocument,
          variables: {
            input: ListCntInput,
          },
          fetchPolicy: "no-cache",
        });
      } catch (err: unknown) {
        console.error(err);
        params.errdialog.value = true;
        if (err instanceof Error) {
          params.errMessage.value =
            "店舗間定期配達一覧件数データの取得に失敗しました。";
        } else {
          params.errMessage.value = "予期しないエラーが発生しました。";
        }
        return;
      }

      // 絞込み後の全データ数 更新
      listDataTotalCount.value =
        result_cnt!.data.getScheduledDeliverytCount?.count !== undefined &&
        result_cnt!.data.getScheduledDeliverytCount?.count !== null
          ? (result_cnt!.data.getScheduledDeliverytCount?.count as number)
          : 0;

      // ページングの表示
      displayPagingRange.value = calcDisplayPagingRange(
        listOptions.value.page - 1,
        listOptions.value.itemsPerPage,
        listDataTotalCount.value
      );
    } catch (err: unknown) {
      params.errdialog.value = true;
      if (err instanceof Error) {
        params.errMessage.value = err.message;
      } else {
        params.errMessage.value = "エラーが発生しました。";
      }
    } finally {
      listLoading.value = false;
    }
  };

  /**
   * 絞り込み検索 @Click
   */
  const onClickSearch = async (
    isUpdateModeBack: () => Promise<boolean>
  ): Promise<void> => {
    if (
      !(await isUpdateModeBack()) &&
      !params.chainSelectValues.value?.chain_code
    ) {
      params.errMessage.value = "チェーンを選択してください";
      params.errdialog.value = true;
      return;
    }

    //一覧チェックボックス初期化
    selectListCheckbox.value = [];

    // 検索ボタンをクリックした場合、ページを1に戻す（既に1ページ目の場合はスキップ）
    if (listOptions.value.page !== 1) {
      isSearchButtonClick.value = true;
      listOptions.value.page = 1;
    }

    //検索する
    await searchQuery();
  };

  /* listOptions の変更監視（ページングなど） */
  watch(
    listOptions,
    async () => {
      // 検索ボタンからの呼び出し時はスキップ（onClickSearchで直接処理）
      if (isSearchButtonClick.value) {
        isSearchButtonClick.value = false;
        return;
      }
      listLoading.value = true;
      await searchQuery();
      listLoading.value = false;
    },
    { deep: true }
  );

  return {
    listLoading,
    listOptions,
    listDataArray,
    listDataTotalCount,
    pageCount,
    selectListCheckbox,
    displayPagingRange,
    isSearchButtonClick,
    getRowProps,
    searchQuery,
    onClickSearch,
  };
};
